import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import MasterLayout from "../../masterLayout/MasterLayout";
import Breadcrumb from "../../components/Breadcrumb";
import { API_URL } from '../../config/api';
import BlingAccountManager from '../../components/bling/BlingAccountManager';

const BlingIntegrationPage = () => {
  const navigate = useNavigate();
  const [regras, setRegras] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [planosProduto, setPlanosProduto] = useState([]);
  const [showRegraModal, setShowRegraModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingPlanos, setLoadingPlanos] = useState(false);
  const [blingConta, setBlingConta] = useState(null);
  const [feedback, setFeedback] = useState({ show: false, type: 'success', message: '' });
  const [editingRegraId, setEditingRegraId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingRegraId, setDeletingRegraId] = useState(null);
  const [deletingRegraName, setDeletingRegraName] = useState('');
  const [regraForm, setRegraForm] = useState({
    produto_id: '',
    plano_id: '',
    sku_bling: '',
    conta_id: '',
    momento_envio: ''
  });

  // Carregar regras e produtos ao montar o componente
  useEffect(() => {
    loadRegras();
    loadProdutos();
  }, []);

  const loadRegras = async () => {
    try {
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/bling/regras`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        setRegras(result.data || []);
      }
    } catch (error) {
      console.error('Erro ao carregar regras:', error);
    }
  };

  const loadProdutos = async () => {
    try {
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/products`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        // Backend retorna { data: [...] }, não { products: [...] }
        setProdutos(result.data || result.products || []);
        console.log('📦 Produtos carregados:', result.data?.length || result.products?.length || 0);
      } else {
        console.error('❌ Erro na resposta:', response.status);
      }
    } catch (error) {
      console.error('Erro ao carregar produtos:', error);
    }
  };

  const loadBlingConta = async () => {
    try {
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/bling/conta-unica`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.data && result.exists) {
          setBlingConta(result.data);
          return result.data;
        }
      }
      setBlingConta(null);
      return null;
    } catch (error) {
      console.error('Erro ao carregar conta Bling:', error);
      setBlingConta(null);
      return null;
    }
  };

  const handleOpenRegraModal = async () => {
    setFeedback({ show: false, type: 'success', message: '' });
    setEditingRegraId(null);
    setRegraForm({ produto_id: '', plano_id: '', sku_bling: '', conta_id: '', momento_envio: '' });
    setPlanosProduto([]);
    setShowRegraModal(true);

    const conta = await loadBlingConta();
    if (conta) {
      setRegraForm(prev => ({ ...prev, conta_id: String(conta.id) }));
    } else {
      setFeedback({ show: true, type: 'warning', message: 'Configure sua conta Bling antes de criar regras.' });
    }
  };

  const handleEditRegra = async (regra) => {
    setFeedback({ show: false, type: 'success', message: '' });
    setEditingRegraId(regra.id);
    setRegraForm({
      produto_id: String(regra.produto_id),
      plano_id: String(regra.plano_id),
      sku_bling: regra.sku_bling,
      conta_id: String(regra.conta_id),
      momento_envio: regra.momento_envio
    });
    setShowRegraModal(true);

    await loadBlingConta();
    if (regra.produto_id) {
      await loadPlanosProduto(regra.produto_id);
    }
  };

  const handleDeleteRegra = (regraId, regraNome) => {
    setDeletingRegraId(regraId);
    setDeletingRegraName(regraNome || 'esta regra');
    setShowDeleteModal(true);
  };

  const confirmDeleteRegra = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/bling/regras/${deletingRegraId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setShowDeleteModal(false);
        setDeletingRegraId(null);
        loadRegras();
        setFeedback({ show: true, type: 'success', message: 'Regra excluída com sucesso!' });
      } else {
        const text = await response.text();
        let message = 'Erro ao excluir regra.';
        try { message = JSON.parse(text).message || message; } catch {}
        setFeedback({ show: true, type: 'danger', message });
      }
    } catch (error) {
      setFeedback({ show: true, type: 'danger', message: 'Erro ao excluir regra. Verifique se o backend está rodando.' });
    } finally {
      setLoading(false);
    }
  };

  const loadPlanosProduto = async (produtoId) => {
    try {
      setLoadingPlanos(true);
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/bling/produtos/${produtoId}/planos`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        setPlanosProduto(result.data?.planos || []);
      }
    } catch (error) {
      console.error('Erro ao carregar planos:', error);
      setPlanosProduto([]);
    } finally {
      setLoadingPlanos(false);
    }
  };

  const handleSaveRegra = async () => {
    try {
      setLoading(true);
      
      if (!regraForm.produto_id || !regraForm.plano_id || !regraForm.sku_bling || !regraForm.conta_id || !regraForm.momento_envio) {
        setFeedback({ show: true, type: 'danger', message: 'Por favor, preencha todos os campos obrigatórios.' });
        return;
      }

      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
      const isEditing = !!editingRegraId;
      const url = isEditing ? `${API_URL}/api/bling/regras/${editingRegraId}` : `${API_URL}/api/bling/regras`;
      const response = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          produto_id: parseInt(regraForm.produto_id),
          plano_id: parseInt(regraForm.plano_id),
          sku_bling: regraForm.sku_bling,
          conta_id: parseInt(regraForm.conta_id),
          momento_envio: regraForm.momento_envio
        })
      });

      if (response.ok) {
        setShowRegraModal(false);
        setEditingRegraId(null);
        setRegraForm({ produto_id: '', plano_id: '', sku_bling: '', conta_id: '', momento_envio: '' });
        setPlanosProduto([]);
        loadRegras();
        setFeedback({ show: true, type: 'success', message: isEditing ? 'Regra atualizada com sucesso!' : 'Regra criada com sucesso!' });
      } else {
        const text = await response.text();
        let message = 'Erro ao salvar regra. Tente novamente.';
        try { message = JSON.parse(text).message || message; } catch {}
        setFeedback({ show: true, type: 'danger', message });
      }
    } catch (error) {
      console.error('❌ Erro ao salvar regra:', error);
      setFeedback({ show: true, type: 'danger', message: 'Erro ao salvar regra. Verifique se o backend está rodando.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <MasterLayout>
      <Breadcrumb title="Bling ERP" />

      <div className="d-flex justify-content-end mb-3">
        <button
          className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
          onClick={() => navigate('/wiki?section=bling')}
        >
          <Icon icon="mdi:book-open-page-variant" width={16} />
          <span className="d-none d-md-inline">Documentacao</span>
        </button>
      </div>

      {feedback.show && !showRegraModal && (
        <Alert variant={feedback.type} dismissible onClose={() => setFeedback({ ...feedback, show: false })} className="mb-4">
          {feedback.message}
        </Alert>
      )}

      <div className="row">
        {/* Coluna Principal - Conta e URL */}
        <div className="col-lg-7 mb-4">
          {/* Card da Conta */}
          <div className="card mb-4">
            <div className="card-header d-flex align-items-center">
              <Icon icon="mdi:account-key" className="me-2" style={{ fontSize: '20px' }} />
              <span className="fw-semibold">Conta Bling</span>
            </div>
            <div className="card-body">
              <BlingAccountManager />
            </div>
          </div>

          {/* Card da URL de Callback */}
          <div className="card border-warning">
            <div className="card-header bg-warning bg-opacity-10 d-flex align-items-center">
              <Icon icon="mdi:link-variant" className="me-2 text-warning" style={{ fontSize: '20px' }} />
              <span className="fw-semibold">URL de Redirecionamento</span>
            </div>
            <div className="card-body">
              <p className="text-muted mb-3">
                Configure esta URL no <a href="https://developer.bling.com.br/aplicativos" target="_blank" rel="noopener noreferrer" className="fw-semibold">painel de desenvolvedor Bling</a> para permitir a autorização:
              </p>
              <div className="d-flex align-items-center gap-2 p-3 bg-dark rounded">
                <code className="text-light flex-grow-1" style={{ fontSize: '13px', wordBreak: 'break-all' }}>
                  {window.location.origin}/bling-callback.html
                </code>
                <Button
                  variant="outline-light"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}/bling-callback.html`);
                    setFeedback({ show: true, type: 'success', message: 'URL copiada para a área de transferência!' });
                  }}
                  title="Copiar URL"
                >
                  <Icon icon="mdi:content-copy" />
                </Button>
              </div>
              <small className="text-muted mt-2 d-block">
                Cole esta URL no campo "URL de Redirecionamento" ao criar sua aplicacao no painel de desenvolvedor Bling.
              </small>
            </div>
          </div>
        </div>

        {/* Coluna Lateral - Regras */}
        <div className="col-lg-5 mb-4">
          <div className="card h-100">
            <div className="card-header d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <Icon icon="mdi:cog-sync" className="me-2" style={{ fontSize: '20px' }} />
                <span className="fw-semibold">Regras de Sincronização</span>
              </div>
              <Button
                variant="primary"
                size="sm"
                onClick={handleOpenRegraModal}
              >
                <Icon icon="mdi:plus" className="me-1" />
                Nova
              </Button>
            </div>
            <div className="card-body p-0">
              {regras.length === 0 ? (
                <div className="text-center py-5">
                  <Icon icon="mdi:cog-off" className="text-muted mb-3" style={{ fontSize: '3rem' }} />
                  <p className="text-muted mb-2">Nenhuma regra configurada</p>
                  <small className="text-muted">Crie regras para sincronizar vendas automaticamente</small>
                </div>
              ) : (
                <div className="list-group list-group-flush">
                  {regras.map((regra) => (
                    <div key={regra.id} className="list-group-item">
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <div className="fw-semibold">{regra.produto_nome}</div>
                          <small className="text-muted">{regra.plano_nome || 'Todos os planos'}</small>
                          <div className="mt-1">
                            <code className="bg-light px-2 py-1 rounded" style={{ fontSize: '11px' }}>
                              {regra.sku_bling}
                            </code>
                          </div>
                        </div>
                        <div className="text-end d-flex flex-column align-items-end gap-1">
                          <div className="d-flex align-items-center gap-1">
                            <span className={`badge ${regra.status === 'ativa' ? 'bg-success' : 'bg-warning'}`}>
                              {regra.status}
                            </span>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="p-0 d-flex align-items-center justify-content-center"
                              style={{ width: '26px', height: '26px' }}
                              onClick={() => handleEditRegra(regra)}
                              title="Editar regra"
                            >
                              <Icon icon="mdi:pencil" style={{ fontSize: '14px' }} />
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              className="p-0 d-flex align-items-center justify-content-center"
                              style={{ width: '26px', height: '26px' }}
                              onClick={() => handleDeleteRegra(regra.id, regra.produto_nome)}
                              title="Excluir regra"
                            >
                              <Icon icon="mdi:delete" style={{ fontSize: '14px' }} />
                            </Button>
                          </div>
                          <small className="text-muted">{regra.momento_envio}</small>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Blocos explicativos */}
      <div className="row g-4 mt-2 mb-4">
        <div className="col-md-4">
          <div className="card h-100 border-0 shadow-sm">
            <div className="card-body text-center">
              <div className="bg-primary bg-opacity-10 rounded-circle p-3 mx-auto mb-3" style={{ width: '64px', height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Icon icon="mdi:account-key" width="28" className="text-primary" />
              </div>
              <span style={{ fontSize: '15px', fontWeight: '600', display: 'block', marginBottom: '8px' }}>1. Conecte sua conta</span>
              <p className="text-muted small mb-0">
                Cadastre as credenciais da sua aplicacao Bling (Client ID e Secret) e autorize o acesso para sincronizar vendas automaticamente.
              </p>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card h-100 border-0 shadow-sm">
            <div className="card-body text-center">
              <div className="bg-success bg-opacity-10 rounded-circle p-3 mx-auto mb-3" style={{ width: '64px', height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Icon icon="mdi:cog-sync" width="28" className="text-success" />
              </div>
              <span style={{ fontSize: '15px', fontWeight: '600', display: 'block', marginBottom: '8px' }}>2. Crie regras de sincronizacao</span>
              <p className="text-muted small mb-0">
                Defina para cada produto e plano qual SKU do Bling deve ser vinculado e em qual momento a venda sera enviada.
              </p>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card h-100 border-0 shadow-sm">
            <div className="card-body text-center">
              <div className="bg-info bg-opacity-10 rounded-circle p-3 mx-auto mb-3" style={{ width: '64px', height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Icon icon="mdi:send-check" width="28" className="text-info" />
              </div>
              <span style={{ fontSize: '15px', fontWeight: '600', display: 'block', marginBottom: '8px' }}>3. Sincronizacao automatica</span>
              <p className="text-muted small mb-0">
                Quando uma venda for confirmada, o pedido e enviado automaticamente para o Bling com os dados do comprador e produto.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Nova Regra */}
      <Modal show={showRegraModal} onHide={() => { setShowRegraModal(false); setEditingRegraId(null); setFeedback({ show: false, type: 'success', message: '' }); }} size="lg">
        <Modal.Header closeButton>
          <span style={{ fontSize: '16px', fontWeight: '600', display: 'flex', alignItems: 'center' }}>
            <Icon icon={editingRegraId ? "mdi:pencil" : "mdi:cog"} className="me-2" />
            {editingRegraId ? 'Editar Regra de Sincronização' : 'Nova Regra de Sincronização'}
          </span>
        </Modal.Header>
        <Modal.Body>
          {feedback.show && (
            <Alert variant={feedback.type} dismissible onClose={() => setFeedback({ ...feedback, show: false })}>
              {feedback.message}
            </Alert>
          )}
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Produto <span className="text-danger">*</span></Form.Label>
              <Form.Select
                value={regraForm.produto_id}
                onChange={(e) => {
                  const produtoId = e.target.value;
                  setRegraForm({
                    ...regraForm, 
                    produto_id: produtoId,
                    plano_id: '',
                    sku_bling: ''
                  });
                  setPlanosProduto([]);
                  if (produtoId) {
                    loadPlanosProduto(produtoId);
                  }
                }}
              >
                <option value="">Selecione um produto</option>
                {produtos.map(produto => (
                  <option key={produto.id} value={produto.id}>
                    {produto.name} - R$ {(produto.price / 100).toFixed(2).replace('.', ',')}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Plano <span className="text-danger">*</span></Form.Label>
              <Form.Select
                value={regraForm.plano_id}
                onChange={(e) => {
                  const planoId = e.target.value;
                  const planoSelecionado = planosProduto.find(p => p.id == planoId);
                  setRegraForm({
                    ...regraForm, 
                    plano_id: planoId,
                    sku_bling: planoSelecionado ? planoSelecionado.sku_code : ''
                  });
                }}
                disabled={!regraForm.produto_id || planosProduto.length === 0}
              >
                <option value="">Selecione um plano</option>
                {planosProduto.map(plano => (
                  <option key={plano.id} value={plano.id}>
                    {plano.name} - R$ {(plano.price / 100).toFixed(2).replace('.', ',')} ({plano.sku_code})
                  </option>
                ))}
              </Form.Select>
              {regraForm.produto_id && loadingPlanos && (
                <div className="text-muted mt-1" style={{ fontSize: '12px' }}>
                  Carregando planos...
                </div>
              )}
              {regraForm.produto_id && !loadingPlanos && planosProduto.length === 0 && (
                <div className="text-warning mt-1" style={{ fontSize: '12px' }}>
                  Nenhum plano encontrado para este produto
                </div>
              )}
              {!regraForm.produto_id && (
                <div className="text-muted mt-1" style={{ fontSize: '12px' }}>
                  Selecione um produto primeiro
                </div>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Código (SKU) do produto na Bling <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                value={regraForm.sku_bling}
                onChange={(e) => setRegraForm({...regraForm, sku_bling: e.target.value})}
                placeholder="Preenchido automaticamente ao selecionar o plano"
                style={{ 
                  backgroundColor: regraForm.sku_bling ? '#f8f9fa' : 'white',
                  borderColor: regraForm.sku_bling ? '#28a745' : '#ced4da'
                }}
              />
              {regraForm.sku_bling && (
                <div className="text-success mt-1" style={{ fontSize: '12px' }}>
                  ✅ SKU preenchido automaticamente do plano selecionado
                </div>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Momento de Envio <span className="text-danger">*</span></Form.Label>
              <Form.Select
                value={regraForm.momento_envio}
                onChange={(e) => setRegraForm({...regraForm, momento_envio: e.target.value})}
              >
                <option value="">Selecione quando enviar</option>
                <option value="Pagamento aprovado">Pagamento aprovado</option>
                <option value="Pagamento agendado">Pagamento agendado</option>
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowRegraModal(false); setEditingRegraId(null); setFeedback({ show: false, type: 'success', message: '' }); }}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveRegra}
            disabled={loading}
          >
            {loading ? 'Salvando...' : (editingRegraId ? 'Atualizar Regra' : 'Salvar Regra')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de Confirmação de Exclusão */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <span style={{ fontSize: '16px', fontWeight: '600', display: 'flex', alignItems: 'center' }}>
            <Icon icon="mdi:alert-circle" className="me-2 text-danger" />
            Confirmar Exclusão
          </span>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">
            Tem certeza que deseja excluir a regra do produto <strong>{deletingRegraName}</strong>? Esta ação não pode ser desfeita.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={confirmDeleteRegra} disabled={loading}>
            {loading ? 'Excluindo...' : 'Excluir Regra'}
          </Button>
        </Modal.Footer>
      </Modal>
    </MasterLayout>
  );
};

export default BlingIntegrationPage;
